import { AfterViewInit, Directive, ElementRef, HostBinding, inject, Input, OnInit } from '@angular/core';
import { NgControl } from '@angular/forms';

import { SelectTrigger } from '@treetop/shared/model';

import { SelectService } from './select.service';

@Directive({
  selector: '[ttSelectTrigger]',
  standalone: true,
})
export class SelectTriggerDirective implements OnInit, AfterViewInit, SelectTrigger {
  protected readonly service = inject(SelectService);
  private readonly el = inject(ElementRef<HTMLElement>) as ElementRef<HTMLElement>;
  protected readonly _ngControl = inject(NgControl, { optional: true });

  @Input() disableAutoWidth = false;

  @HostBinding('attr.role') role = 'combobox';
  @HostBinding('attr.id') get selectTriggerId() {
    return `${this.service.id()}--trigger`;
  }
  @HostBinding('attr.aria-expanded') get isExpanded() {
    return this.service.isExpanded();
  }
  @HostBinding('attr.aria-controls') get selectContentId() {
    return `${this.service.id()}--content`;
  }
  @HostBinding('attr.aria-autocomplete') ariaAutocomplete = 'none';
  @HostBinding('class.ng-invalid') get ngInvalid() {
    return this._ngControl?.invalid || null;
  }
  @HostBinding('class.ng-dirty') get ngDirty() {
    return this._ngControl?.dirty || null;
  }
  @HostBinding('class.ng-valid') get ngValid() {
    return this._ngControl?.valid || null;
  }
  @HostBinding('class.ng-touched') get ngTouched() {
    return this._ngControl?.touched || null;
  }
  @HostBinding('class.ng-untouched') get ngUntouched() {
    return this._ngControl?.untouched || null;
  }
  @HostBinding('class.ng-pristine') get ngPristine() {
    return this._ngControl?.pristine || null;
  }
  @HostBinding('attr.type') type = 'button';

  public ngOnInit() {
    if (!this.service) return;

    if (this.disableAutoWidth) return;

    this.service.setSelectTrigger(this);

    const resizeObserver = new ResizeObserver((entries) => {
      this.service.updateState({ triggerWidth: entries[0].borderBoxSize[0].inlineSize });
    });
    resizeObserver.observe(this.el.nativeElement);
  }

  ngAfterViewInit() {
    if (this.disableAutoWidth) return;
    this.service.updateState({ triggerWidth: this.el.nativeElement.offsetWidth });
  }

  /**
   * Sets focus on the native element of the select trigger directive.
   */
  public focus() {
    this.el.nativeElement.focus();
  }
}
